import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Ban, Clock, Download, Gift, History, QrCode, RefreshCw, ScanLine, Star, Trash2, X,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const STATUS_LABELS = {
  en_attente_paiement: "Paiement en attente",
  active: "Active",
  partielle: "Partiellement utilisée",
  utilisee: "Entièrement utilisée",
  expiree: "Expirée",
  annulee: "Annulée",
  remboursee: "Remboursée",
  suspendue: "Suspendue",
};

const STATUS_COLORS = {
  en_attente_paiement: "border-white/20 text-ivory/50",
  active: "border-gold/40 text-gold",
  partielle: "border-copper/50 text-copper",
  utilisee: "border-white/20 text-ivory/40",
  expiree: "border-red-400/40 text-red-400",
  annulee: "border-white/20 text-ivory/40",
  remboursee: "border-white/20 text-ivory/40",
  suspendue: "border-red-400/40 text-red-400",
};

const fmt = (n) => Number(n || 0).toFixed(2).replace(".", ",") + " €";
const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("fr-FR", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso || "—";
  }
};

export default function GiftCardsManager() {
  const [sub, setSub] = useState("cartes");
  const [cards, setCards] = useState([]);
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState({ q: "", status: "", date_from: "", date_to: "" });
  const [detail, setDetail] = useState(null);
  const [loyalty, setLoyalty] = useState(null);
  const [cfg, setCfg] = useState(null);

  const loadCards = useCallback(() => {
    const params = new URLSearchParams(Object.entries(filters).filter(([, v]) => v));
    api.get(`/admin/gift-cards?${params}`).then((r) => setCards(r.data)).catch(() => {});
    api.get("/admin/gift-cards/stats").then((r) => setStats(r.data)).catch(() => {});
  }, [filters]);

  useEffect(() => {
    loadCards();
  }, [loadCards]);

  useEffect(() => {
    if (sub === "fidelite") api.get("/admin/fidelite").then((r) => setLoyalty(r.data)).catch(() => {});
    if (sub === "reglages") api.get("/programme").then((r) => setCfg(r.data)).catch(() => {});
  }, [sub]);

  const openDetail = async (id) => {
    try {
      const { data } = await api.get(`/admin/gift-cards/${id}`);
      setDetail(data);
    } catch {
      toast.error("Carte introuvable.");
    }
  };

  const exportCsv = async () => {
    const r = await api.get("/admin/gift-cards/export.csv", { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = "cartes-cadeaux.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="giftcards-manager">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold text-ivory">Cartes cadeaux & fidélité</h2>
        <div className="flex gap-1.5">
          {[
            { id: "cartes", label: "Cartes cadeaux", icon: Gift },
            { id: "fidelite", label: "Fidélité", icon: Star },
            { id: "reglages", label: "Réglages", icon: RefreshCw },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setSub(t.id)}
              className={`flex items-center gap-2 rounded-md border px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.15em] transition-colors ${sub === t.id ? "border-copper bg-copper text-ivory" : "border-white/10 text-ivory/60 hover:border-copper/50"}`}
              data-testid={`gc-subtab-${t.id}`}
            >
              <t.icon className="h-3.5 w-3.5" strokeWidth={1.5} />
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {sub === "cartes" && (
        <>
          {stats && (
            <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="gc-stats">
              {[
                { l: "Cartes vendues", v: stats.sold_count },
                { l: "Total vendu", v: fmt(stats.total_sold) },
                { l: "Total utilisé", v: fmt(stats.total_used) },
                { l: "Encore disponible", v: fmt(stats.total_available) },
                { l: "Expiré non utilisé", v: fmt(stats.expired_unused) },
                { l: "Frais Stripe", v: fmt(stats.stripe_fees) },
                { l: "Net reçu", v: fmt(stats.net_received) },
                { l: "Expire bientôt (30 j)", v: stats.expiring_soon },
              ].map((s) => (
                <div key={s.l} className="rounded-lg border border-white/10 bg-coal/60 p-4">
                  <p className="text-[10px] uppercase tracking-[0.15em] text-ivory/40">{s.l}</p>
                  <p className="mt-1 text-xl font-semibold text-ivory">{s.v}</p>
                </div>
              ))}
            </div>
          )}

          <div className="mb-5 flex flex-wrap items-center gap-2" data-testid="gc-filters">
            <input
              value={filters.q}
              onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
              placeholder="Numéro, acheteur, bénéficiaire, email…"
              className="input-dark !h-10 !w-64 !text-xs"
              data-testid="gc-filter-q"
            />
            <select
              value={filters.status}
              onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
              className="input-dark !h-10 !w-48 !text-xs"
              data-testid="gc-filter-status"
            >
              <option value="">Tous les statuts</option>
              {Object.entries(STATUS_LABELS).map(([v, l]) => (
                <option key={v} value={v}>{l}</option>
              ))}
            </select>
            <input type="date" value={filters.date_from} onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))} className="input-dark !h-10 !w-36 !text-xs [color-scheme:dark]" data-testid="gc-filter-from" />
            <input type="date" value={filters.date_to} onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))} className="input-dark !h-10 !w-36 !text-xs [color-scheme:dark]" data-testid="gc-filter-to" />
            <button onClick={exportCsv} className="btn-outline !h-10 !px-4 !text-[11px]" data-testid="gc-export-csv">
              <Download className="h-3.5 w-3.5" strokeWidth={1.5} />
              Export CSV
            </button>
          </div>

          <div className="space-y-2" data-testid="gc-list">
            {cards.length === 0 && <p className="text-sm text-ivory/40" data-testid="gc-empty">Aucune carte cadeau.</p>}
            {cards.map((c) => (
              <button key={c.id} onClick={() => openDetail(c.id)} className="flex w-full flex-wrap items-center gap-x-5 gap-y-1.5 rounded-lg border border-white/10 bg-coal/60 px-5 py-3.5 text-left transition-colors hover:border-gold/30" data-testid={`gc-row-${c.id}`}>
                <span className="font-mono text-sm tracking-widest text-gold">{c.code}</span>
                <span className={`rounded border px-2 py-0.5 text-[11px] font-semibold ${STATUS_COLORS[c.status]}`}>{STATUS_LABELS[c.status]}</span>
                <span className="text-sm text-ivory">{fmt(c.amount)}</span>
                <span className="text-xs text-ivory/50">solde {fmt(c.balance ?? c.amount)}</span>
                <span className="text-xs text-ivory/40">{c.buyer_name}{c.recipient_name ? ` → ${c.recipient_name}` : ""}</span>
                <span className="ml-auto text-xs text-ivory/35">expire {c.expires_at ? new Date(c.expires_at).toLocaleDateString("fr-FR") : "—"}</span>
              </button>
            ))}
          </div>
        </>
      )}

      {sub === "fidelite" && <LoyaltyPanel data={loyalty} reload={() => api.get("/admin/fidelite").then((r) => setLoyalty(r.data))} />}
      {sub === "reglages" && cfg && <ProgrammeSettings cfg={cfg} onSaved={(c) => setCfg(c)} />}

      {detail && (
        <CardDetailModal
          detail={detail}
          onClose={() => setDetail(null)}
          onChanged={() => {
            setDetail(null);
            loadCards();
          }}
        />
      )}
    </div>
  );
}

function CardDetailModal({ detail, onClose, onChanged }) {
  const { card, events } = detail;
  const [useAmount, setUseAmount] = useState("");
  const [useRef, setUseRef] = useState("");
  const [idemKey, setIdemKey] = useState(() => crypto.randomUUID());
  const [busy, setBusy] = useState(false);
  const [prolongOpen, setProlongOpen] = useState(false);
  const [refundOpen, setRefundOpen] = useState(false);
  const [prolong, setProlong] = useState({ new_expiry: "", reason: "" });
  const [reason, setReason] = useState("");

  const usable = card.status === "active" || card.status === "partielle";

  const use = async () => {
    const amount = parseFloat((useAmount || "").replace(",", "."));
    if (!amount || amount <= 0) {
      toast.error("Montant invalide.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/gift-cards/${card.id}/use`, {
        amount, reference: useRef, idempotency_key: idemKey,
      });
      toast.success(data.duplicate ? "Opération déjà enregistrée (aucun double débit)." : `Débité : ${fmt(amount)} — nouveau solde ${fmt(data.balance)}.`);
      setIdemKey(crypto.randomUUID());
      onChanged();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const setStatus = async (status) => {
    try {
      await api.patch(`/admin/gift-cards/${card.id}`, { status });
      toast.success("Statut mis à jour.");
      onChanged();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const doProlong = async () => {
    try {
      await api.post(`/admin/gift-cards/${card.id}/prolong`, prolong);
      toast.success("Validité prolongée (geste commercial enregistré).");
      onChanged();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const doRefund = async () => {
    try {
      await api.post(`/admin/gift-cards/${card.id}/refund`, { reason });
      toast.success("Carte remboursée et désactivée.");
      onChanged();
    } catch (err) {
      const msg = err.response?.status === 502
        ? "Remboursement impossible : aucun paiement Stripe rattaché ou Stripe injoignable — traitez-le manuellement."
        : formatApiError(err.response?.data?.detail);
      toast.error(msg);
    }
  };

  const remove = async () => {
    if (!window.confirm("Supprimer définitivement cette carte ?")) return;
    try {
      await api.delete(`/admin/gift-cards/${card.id}`);
      toast.success("Carte supprimée.");
      onChanged();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm" data-testid="gc-detail-modal">
      <div className="max-h-[90vh] w-full max-w-2xl space-y-5 overflow-y-auto rounded-lg border border-white/10 bg-coal p-8">
        <div className="flex items-start justify-between">
          <div>
            <p className="font-mono text-lg tracking-widest text-gold">{card.code}</p>
            <span className={`mt-1 inline-block rounded border px-2 py-0.5 text-[11px] font-semibold ${STATUS_COLORS[card.status]}`}>{STATUS_LABELS[card.status]}</span>
          </div>
          <div className="flex items-center gap-2">
            <img src={`${process.env.REACT_APP_BACKEND_URL}/api/gift-cards/${card.code}/qr.png`} alt="QR" className="h-16 w-16 rounded border border-white/10 bg-white p-1" data-testid="gc-detail-qr" />
            <button onClick={onClose} aria-label="Fermer"><X className="h-5 w-5 text-ivory/50 hover:text-ivory" strokeWidth={1.5} /></button>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-3" data-testid="gc-detail-info">
          <div><p className="text-[10px] uppercase text-ivory/40">Montant initial</p><p className="text-ivory">{fmt(card.amount)}</p></div>
          <div><p className="text-[10px] uppercase text-ivory/40">Solde restant</p><p className="font-semibold text-gold">{fmt(card.balance ?? card.amount)}</p></div>
          <div><p className="text-[10px] uppercase text-ivory/40">Expire le</p><p className="text-ivory">{card.expires_at ? new Date(card.expires_at).toLocaleDateString("fr-FR") : "—"}</p></div>
          <div><p className="text-[10px] uppercase text-ivory/40">Acheteur</p><p className="text-ivory/80">{card.buyer_name}<br /><span className="text-xs text-ivory/45">{card.buyer_email}</span></p></div>
          <div><p className="text-[10px] uppercase text-ivory/40">Bénéficiaire</p><p className="text-ivory/80">{card.recipient_name || "—"}<br /><span className="text-xs text-ivory/45">{card.recipient_email || "—"}</span></p></div>
          <div><p className="text-[10px] uppercase text-ivory/40">Envoi</p><p className="text-ivory/80">{card.send_status === "envoyee" ? `Envoyée ${card.sent_at ? fmtDate(card.sent_at) : ""}` : card.send_status === "programme" ? `Programmée le ${card.send_date}` : "PDF / remise en main propre"}</p></div>
          {card.stripe_fee != null && <div><p className="text-[10px] uppercase text-ivory/40">Frais Stripe</p><p className="text-ivory/80">{fmt(card.stripe_fee)}</p></div>}
          {card.net_amount != null && <div><p className="text-[10px] uppercase text-ivory/40">Net reçu</p><p className="text-ivory/80">{fmt(card.net_amount)}</p></div>}
        </div>
        {card.message && <p className="rounded-md border border-white/10 p-3 text-xs italic text-ivory/60">« {card.message} »</p>}

        {usable && (
          <div className="space-y-3 rounded-md border border-copper/30 bg-copper/5 p-4" data-testid="gc-use-panel">
            <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.15em] text-copper">
              <ScanLine className="h-4 w-4" strokeWidth={1.5} />
              Utiliser la carte (débit)
            </p>
            <div className="flex flex-wrap gap-2">
              <input value={useAmount} onChange={(e) => setUseAmount(e.target.value)} placeholder="Montant (ex : 25)" className="input-dark !h-10 !w-36 !text-sm" data-testid="gc-use-amount" />
              <input value={useRef} onChange={(e) => setUseRef(e.target.value)} placeholder="Réf. ticket/commande (optionnel)" className="input-dark !h-10 !w-56 !text-sm" data-testid="gc-use-ref" />
              <button onClick={() => setUseAmount(String(card.balance ?? card.amount))} className="btn-outline !h-10 !px-3 !text-[11px]" data-testid="gc-use-all">Tout utiliser</button>
              <button onClick={use} disabled={busy} className="btn-primary !h-10 !px-5 !text-[11px]" data-testid="gc-use-submit">
                {busy ? "Débit…" : "Débiter"}
              </button>
            </div>
            <p className="text-[11px] text-ivory/35">Protégé contre les doubles débits (double clic, double scan, rafraîchissement).</p>
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          {card.status === "active" && <button onClick={() => setStatus("suspendue")} className="btn-outline !h-9 !px-3 !text-[11px]" data-testid="gc-suspend"><Ban className="h-3.5 w-3.5" strokeWidth={1.5} />Suspendre</button>}
          {card.status === "suspendue" && <button onClick={() => setStatus("active")} className="btn-outline !h-9 !px-3 !text-[11px]" data-testid="gc-reactivate">Réactiver</button>}
          {card.status !== "remboursee" && card.status !== "en_attente_paiement" && (
            <button onClick={() => setRefundOpen(!refundOpen)} className="btn-outline !h-9 !px-3 !text-[11px]" data-testid="gc-refund-open">Rembourser</button>
          )}
          <button onClick={() => setProlongOpen(!prolongOpen)} className="btn-outline !h-9 !px-3 !text-[11px]" data-testid="gc-prolong-open"><Clock className="h-3.5 w-3.5" strokeWidth={1.5} />Prolonger (exceptionnel)</button>
          <button onClick={remove} className="btn-outline !h-9 !px-3 !text-[11px] !text-red-400/80 hover:!border-red-400" data-testid="gc-delete"><Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />Supprimer</button>
        </div>

        {prolongOpen && (
          <div className="space-y-2 rounded-md border border-gold/25 bg-gold/5 p-4" data-testid="gc-prolong-panel">
            <p className="text-xs text-ivory/60">Geste commercial exceptionnel — réservé au super-administrateur, avec motif obligatoire et traçabilité.</p>
            <input type="date" value={prolong.new_expiry} onChange={(e) => setProlong((p) => ({ ...p, new_expiry: e.target.value }))} className="input-dark !h-10 !w-44 !text-xs [color-scheme:dark]" data-testid="gc-prolong-date" />
            <input value={prolong.reason} onChange={(e) => setProlong((p) => ({ ...p, reason: e.target.value }))} placeholder="Motif (obligatoire)" className="input-dark !h-10 !text-xs" data-testid="gc-prolong-reason" />
            <button onClick={doProlong} className="btn-primary !h-9 !px-4 !text-[11px]" data-testid="gc-prolong-submit">Prolonger</button>
          </div>
        )}
        {refundOpen && (
          <div className="space-y-2 rounded-md border border-red-400/25 bg-red-400/5 p-4" data-testid="gc-refund-panel">
            <p className="text-xs text-ivory/60">
              Remboursement Stripe du solde restant ({fmt(card.balance ?? card.amount)}) — la carte sera désactivée.
              {card.amount > (card.balance ?? card.amount) && ` Déjà consommé : ${fmt(card.amount - (card.balance ?? card.amount))} (non remboursé).`}
            </p>
            <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Motif (obligatoire)" className="input-dark !h-10 !text-xs" data-testid="gc-refund-reason" />
            <button onClick={doRefund} className="btn-primary !h-9 !px-4 !text-[11px]" data-testid="gc-refund-submit">Confirmer le remboursement</button>
          </div>
        )}

        {(card.uses || []).length > 0 && (
          <div data-testid="gc-uses">
            <p className="mb-2 text-xs font-semibold uppercase tracking-[0.2em] text-ivory/50">Utilisations</p>
            <div className="space-y-1">
              {card.uses.map((u) => (
                <div key={u.id} className="flex flex-wrap items-center gap-x-4 text-xs text-ivory/50">
                  <span>{fmtDate(u.at)}</span>
                  <span className={u.amount >= 0 ? "text-copper" : "text-gold"}>{u.amount >= 0 ? `-${fmt(u.amount)}` : `+${fmt(-u.amount)}`}</span>
                  <span>solde {fmt(u.balance_before)} → {fmt(u.balance_after)}</span>
                  <span className="text-ivory/30">{u.staff}{u.reference ? ` · ${u.reference}` : ""}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {(card.extensions || []).length > 0 && (
          <div data-testid="gc-extensions">
            <p className="mb-2 text-xs font-semibold uppercase tracking-[0.2em] text-ivory/50">Prolongations exceptionnelles</p>
            {card.extensions.map((e, i) => (
              <p key={i} className="text-xs text-ivory/50">
                {new Date(e.old_expiry).toLocaleDateString("fr-FR")} → {new Date(e.new_expiry).toLocaleDateString("fr-FR")} · {e.reason} · par {e.decided_by} le {fmtDate(e.decided_at)}
              </p>
            ))}
          </div>
        )}

        {events.length > 0 && (
          <div data-testid="gc-events">
            <p className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-ivory/50">
              <History className="h-3.5 w-3.5" strokeWidth={1.5} />
              Journal d'audit
            </p>
            <div className="max-h-40 space-y-1 overflow-y-auto">
              {events.map((e) => (
                <p key={e.id} className="text-[11px] text-ivory/40">
                  {fmtDate(e.created_at)} · <strong className="text-ivory/60">{e.type}</strong> — {e.detail} <span className="text-ivory/25">({e.actor})</span>
                </p>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function LoyaltyPanel({ data, reload }) {
  const [adj, setAdj] = useState({ email: "", points: "", reason: "" });
  const [achat, setAchat] = useState({ email: "", amount: "" });
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);

  if (!data) return <p className="text-sm text-ivory/40">Chargement…</p>;

  const adjust = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/fidelite/ajustement", { ...adj, points: parseInt(adj.points, 10) });
      toast.success("Correction de points enregistrée.");
      setAdj({ email: "", points: "", reason: "" });
      reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const creditPurchase = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/fidelite/achat", { email: achat.email, amount_eur: achat.amount });
      toast.success(`Achat crédité pour ${achat.email}.`);
      setAchat({ email: "", amount: "" });
      reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const validateReward = async (id) => {
    try {
      await api.post(`/admin/fidelite/recompenses/${id}/valider`);
      toast.success("Récompense marquée utilisée.");
      reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const q = search.trim().toLowerCase();
  const filteredClients = q
    ? data.clients.filter((c) =>
        [c.name, c.email, c.card_number].filter(Boolean).some((v) => v.toLowerCase().includes(q))
      )
    : data.clients;

  return (
    <div className="space-y-8" data-testid="loyalty-panel">
      {/* Enregistrer un achat en caisse (montant en euros) */}
      <form onSubmit={creditPurchase} className="space-y-3 rounded-lg border border-gold/30 bg-coal/60 p-6" data-testid="loyalty-purchase-form">
        <h3 className="text-sm font-semibold uppercase tracking-[0.2em] text-gold">Enregistrer un achat</h3>
        <p className="text-xs text-ivory/45">
          Après le règlement du client, saisissez le montant : les points se créditent automatiquement
          ({data.config.loyalty_points_per_euro} € = {data.config.loyalty_points_per_euro} pt, bon de {fmt(data.config.loyalty_reward_value)} dès {data.config.loyalty_reward_threshold} pts).
        </p>
        <div className="grid gap-3 sm:grid-cols-[1fr_160px_auto]">
          <input required type="email" value={achat.email} onChange={(e) => setAchat((a) => ({ ...a, email: e.target.value }))} placeholder="E-mail du client (cliquez un client ci-dessous)" className="input-dark !h-10 !text-xs" data-testid="loyalty-purchase-email" />
          <input required type="number" min="0.5" step="0.01" value={achat.amount} onChange={(e) => setAchat((a) => ({ ...a, amount: e.target.value }))} placeholder="Montant (€)" className="input-dark !h-10 !text-xs" data-testid="loyalty-purchase-amount" />
          <button type="submit" disabled={busy} className="btn-primary !h-10 !px-4 !text-[11px]" data-testid="loyalty-purchase-submit">
            {busy ? "Enregistrement…" : "Créditer les points"}
          </button>
        </div>
      </form>

      {/* Clients fidélité : recherche nom / e-mail / numéro */}
      <div className="rounded-lg border border-white/10 bg-coal/60 p-6" data-testid="loyalty-clients">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <h3 className="text-sm font-semibold uppercase tracking-[0.2em] text-gold">Clients fidélité ({filteredClients.length})</h3>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher : nom, e-mail ou n° de carte…"
            className="input-dark !h-9 !w-72 !text-xs"
            data-testid="loyalty-search-input"
          />
        </div>
        {filteredClients.length === 0 ? (
          <p className="text-sm text-ivory/40">
            {q ? "Aucun client ne correspond à cette recherche." : "Aucun client pour le moment."}
          </p>
        ) : (
          <div className="space-y-2">
            {filteredClients.map((c) => (
              <div key={c.email} className="flex flex-wrap items-center gap-x-5 gap-y-1 rounded-md border border-white/10 px-4 py-2.5" data-testid={`loyalty-client-${c.email}`}>
                <span className="text-sm text-ivory">{c.name || c.email}</span>
                <span className="text-xs text-ivory/45">{c.email}</span>
                {c.card_number && <span className="font-mono text-xs tracking-widest text-ivory/50">{c.card_number}</span>}
                <span className="ml-auto font-semibold text-gold">{c.points} pts</span>
                <button
                  onClick={() => setAchat((a) => ({ ...a, email: c.email }))}
                  className="btn-outline !h-8 !px-3 !text-[10px]"
                  data-testid={`loyalty-credit-${c.email}`}
                >
                  Créditer un achat
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* QR code comptoir à imprimer */}
      <div className="rounded-lg border border-white/10 bg-coal/60 p-6" data-testid="loyalty-counter-qr">
        <h3 className="mb-2 text-sm font-semibold uppercase tracking-[0.2em] text-gold">QR code du comptoir</h3>
        <p className="text-xs leading-relaxed text-ivory/45">
          Imprimez ce QR code et affichez-le au comptoir : le client le scanne avec son téléphone,
          crée son compte gratuitement et retrouve sa carte de fidélité numérique.
        </p>
        <div className="mt-4 flex items-center gap-5">
          <img
            src={`${process.env.REACT_APP_BACKEND_URL}/api/admin/fidelite/qr-comptoir.png`}
            alt="QR code comptoir — page fidélité"
            className="h-28 w-28 rounded-lg border border-gold/30 bg-white p-2"
            data-testid="loyalty-counter-qr-image"
          />
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/fidelite/qr-comptoir.png`}
            target="_blank"
            rel="noreferrer"
            className="btn-outline !h-9 !px-4 !text-[11px]"
            data-testid="loyalty-counter-qr-open"
          >
            Ouvrir pour imprimer
          </a>
        </div>
      </div>

      {/* Correction manuelle de points */}
      <form onSubmit={adjust} className="space-y-3 rounded-lg border border-white/10 bg-coal/60 p-6" data-testid="loyalty-adjust-form">
        <h3 className="text-sm font-semibold uppercase tracking-[0.2em] text-gold">Correction manuelle de points</h3>
        <div className="grid gap-3 sm:grid-cols-3">
          <input required type="email" value={adj.email} onChange={(e) => setAdj((a) => ({ ...a, email: e.target.value }))} placeholder="Email du client" className="input-dark !h-10 !text-xs" data-testid="loyalty-adj-email" />
          <input required value={adj.points} onChange={(e) => setAdj((a) => ({ ...a, points: e.target.value }))} placeholder="Points (+ ou -)" className="input-dark !h-10 !text-xs" data-testid="loyalty-adj-points" />
          <input required value={adj.reason} onChange={(e) => setAdj((a) => ({ ...a, reason: e.target.value }))} placeholder="Motif (obligatoire)" className="input-dark !h-10 !text-xs" data-testid="loyalty-adj-reason" />
        </div>
        <button type="submit" disabled={busy} className="btn-primary !h-9 !px-4 !text-[11px]" data-testid="loyalty-adj-submit">
          {busy ? "Enregistrement…" : "Appliquer la correction"}
        </button>
      </form>

      <div className="rounded-lg border border-white/10 bg-coal/60 p-6" data-testid="loyalty-rewards-admin">
        <h3 className="mb-4 text-sm font-semibold uppercase tracking-[0.2em] text-gold">Récompenses ({data.rewards.length})</h3>
        {data.rewards.length === 0 ? (
          <p className="text-sm text-ivory/40">Aucune récompense convertie pour le moment (seuil : {data.config.loyalty_reward_threshold} pts = {fmt(data.config.loyalty_reward_value)}).</p>
        ) : (
          <div className="space-y-2">
            {data.rewards.map((r) => (
              <div key={r.id} className="flex flex-wrap items-center gap-x-5 rounded-md border border-white/10 px-4 py-2.5" data-testid={`reward-row-${r.id}`}>
                <span className="font-mono text-sm tracking-widest text-gold">{r.code}</span>
                <span className="text-sm text-ivory/70">{fmt(r.value)}</span>
                <span className="text-xs text-ivory/45">{r.email}</span>
                <span className={`ml-auto text-[11px] font-semibold uppercase ${r.status === "disponible" ? "text-gold" : "text-ivory/35"}`}>
                  {r.status === "disponible" ? "Disponible" : "Utilisée"}
                </span>
                {r.status === "disponible" && (
                  <button onClick={() => validateReward(r.id)} className="btn-outline !h-8 !px-3 !text-[10px]" data-testid={`reward-validate-${r.id}`}>Marquer utilisée</button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ProgrammeSettings({ cfg, onSaved }) {
  const [form, setForm] = useState({
    gc_min_amount: cfg.gc_min_amount,
    gc_max_amount: cfg.gc_max_amount,
    gc_preset_amounts: (cfg.gc_preset_amounts || []).join(", "),
    gc_validity_months: cfg.gc_validity_months,
    loyalty_points_per_euro: cfg.loyalty_points_per_euro,
    loyalty_reward_threshold: cfg.loyalty_reward_threshold,
    loyalty_reward_value: cfg.loyalty_reward_value,
  });
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        gc_min_amount: parseFloat(form.gc_min_amount),
        gc_max_amount: parseFloat(form.gc_max_amount),
        gc_preset_amounts: form.gc_preset_amounts.split(",").map((s) => parseFloat(s.trim())).filter((n) => !isNaN(n) && n > 0),
        gc_validity_months: parseInt(form.gc_validity_months, 10),
        loyalty_points_per_euro: parseFloat(form.loyalty_points_per_euro),
        loyalty_reward_threshold: parseInt(form.loyalty_reward_threshold, 10),
        loyalty_reward_value: parseFloat(form.loyalty_reward_value),
      };
      const { data } = await api.put("/admin/programme", payload);
      toast.success("Programme mis à jour (les cartes déjà vendues gardent leur date d'expiration).");
      onSaved({ ...data, gc_expiry_rule: cfg.gc_expiry_rule });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const num = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <form onSubmit={save} className="max-w-2xl space-y-6 rounded-lg border border-white/10 bg-coal/60 p-8" data-testid="programme-settings">
      <div>
        <h3 className="text-sm font-semibold uppercase tracking-[0.2em] text-gold">Cartes cadeaux</h3>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ivory/50">Montant minimum (€)</label>
            <input type="number" value={form.gc_min_amount} onChange={num("gc_min_amount")} className="input-dark !h-11" data-testid="cfg-gc-min" />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ivory/50">Montant maximum (€)</label>
            <input type="number" value={form.gc_max_amount} onChange={num("gc_max_amount")} className="input-dark !h-11" data-testid="cfg-gc-max" />
          </div>
          <div className="sm:col-span-2">
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ivory/50">Montants proposés (séparés par des virgules)</label>
            <input value={form.gc_preset_amounts} onChange={num("gc_preset_amounts")} className="input-dark !h-11" data-testid="cfg-gc-presets" />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ivory/50">Validité des nouvelles cartes (mois)</label>
            <input type="number" min="1" max="36" value={form.gc_validity_months} onChange={num("gc_validity_months")} className="input-dark !h-11" data-testid="cfg-gc-validity" />
          </div>
        </div>
        <p className="mt-2 text-[11px] text-ivory/40">La durée ne s'applique qu'aux futures cartes — jamais rétroactivement.</p>
      </div>
      <div className="border-t border-white/10 pt-6">
        <h3 className="text-sm font-semibold uppercase tracking-[0.2em] text-gold">Fidélité</h3>
        <div className="mt-4 grid gap-4 sm:grid-cols-3">
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ivory/50">Points par € dépensé</label>
            <input type="number" step="0.5" value={form.loyalty_points_per_euro} onChange={num("loyalty_points_per_euro")} className="input-dark !h-11" data-testid="cfg-loyalty-rate" />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ivory/50">Seuil récompense (points)</label>
            <input type="number" value={form.loyalty_reward_threshold} onChange={num("loyalty_reward_threshold")} className="input-dark !h-11" data-testid="cfg-loyalty-threshold" />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ivory/50">Valeur récompense (€)</label>
            <input type="number" value={form.loyalty_reward_value} onChange={num("loyalty_reward_value")} className="input-dark !h-11" data-testid="cfg-loyalty-value" />
          </div>
        </div>
      </div>
      <button type="submit" disabled={saving} className="btn-primary" data-testid="cfg-save-button">
        {saving ? "Enregistrement…" : "Enregistrer le programme"}
      </button>
    </form>
  );
}
